import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import config from '../../../config';
import {
  Button,
  Card,
  Container,
  Heading,
  Icon,
  Spinner,
  useToast,
} from '../../../npaper';
import { SideSheet, ISideSheetProps } from '../../../npaper/Dialog';
import { IBilling, useStores } from '../../../stores';
import { Logo } from '../../Logo';
import {
  CreditCardForm,
  PoweredByStripe,
  StripeWrapper,
} from './CreditCardDialog';

interface IUpgradeSideSheetProps extends Omit<ISideSheetProps, 'title'> {}

const UpgradeSideSheetWrapped: React.FC<IUpgradeSideSheetProps> = (props) => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const toaster = useToast();

  const [billing, setBilling] = useState<IBilling>();
  const [addCard, setAddCard] = useState(true);

  const { authStore, organisationStore } = useStores();
  const organisation = authStore.currentOrganisation!;

  const [billingError, setBillingError] = useState<any>();

  const { errors, control, setError, handleSubmit } =
    useForm<{ card?: boolean }>();

  useEffect(() => {
    if (!props.isShown) return;
    let isSub = true;
    setBilling(undefined);
    organisationStore
      .getBilling(organisation)
      .then((billing) => {
        if (isSub) {
          setBilling(billing);
          setAddCard(!billing.card);
        }
      })
      .catch((err) => isSub && setBillingError(err));
    return () => {
      isSub = false;
    };
  }, [organisation, organisationStore, props.isShown]);

  const shouldReactivate =
    !authStore.currentOrganisation!.subscription ||
    !authStore.currentOrganisation!.subscription.active;

  const onSubmit = async ({ card = false }: { card?: boolean }) => {
    if (card) {
      if (!stripe || !elements) return;
      try {
        // Create an intent from API
        const setupIntent = await organisationStore.getSetupIntent(
          authStore.currentOrganisation!
        );
        // Confirm the intent
        const cardElement = elements.getElement(CardElement);
        await stripe.confirmCardSetup(setupIntent.client_secret!, {
          payment_method: {
            card: cardElement!,
          },
        });
        await organisationStore.getSetupIntent(
          authStore.currentOrganisation!,
          setupIntent.id
        );
      } catch (err) {
        console.error(err);
        setError('card', {
          type: 'StripeError',
          message: intl.formatMessage({
            defaultMessage:
              'Could not setup the payment method. Please try again or contact support.',
          }),
        });
        return;
      }
    }

    try {
      await organisationStore.subscribe(organisation);
      toaster.addToast({
        intent: 'success',
        message: intl.formatMessage({
          defaultMessage: 'You are now on a paid subscription',
        }),
        timeout: 5000,
      });
      props.onClose && props.onClose();
    } catch (err) {
      console.error(err);
      toaster.showDefaultError(err);
    }
  };

  return (
    <SideSheet
      {...props}
      title={
        shouldReactivate
          ? intl.formatMessage({
              defaultMessage: 'Reactivate your subscription',
            })
          : intl.formatMessage({
              defaultMessage: 'Upgrade your subscription',
            })
      }
    >
      <Container className="mx-auto max-w-xl py-16">
        <div className="text-center mb-4">
          <Heading size={500}>
            <FormattedMessage defaultMessage="Upgrade your Sails account" />
          </Heading>
          <p>
            <FormattedMessage defaultMessage="To continue using Sails, please upgrade to a paid subscription" />
          </p>
        </div>

        {!billing ? (
          <div className="mx-auto ">
            {billingError &&
              billingError.response &&
              billingError.response.status === 403 && (
                <div className="w-full text-center">
                  <p className="danger">
                    <FormattedMessage defaultMessage="Only admins can opgrade to a paid subscription plan. Please contact your administrator, and ask them to upgrade Sails." />
                  </p>
                </div>
              )}
            {!billingError && (
              <div className="max-w-min mx-auto">
                <Spinner />
              </div>
            )}
          </div>
        ) : (
          <>
            <Card className="bg-white p-8 flex">
              <div className="flex-grow-0 flex-shrink-0 w-16 h-16 rounded-full bg-cyan-800 flex items-center justify-center mr-4 shadow-lg">
                <Logo />
              </div>
              <div className="w-full">
                <div className="flex items-baseline justify-between">
                  <Heading size={400}>Sails</Heading>
                  <p className="my-0">
                    <span className="text-base font-semibold mr-1">
                      <FormattedNumber
                        format="currency"
                        currency="DKK"
                        value={
                          (billing.plans[0].amount / 100) *
                          organisation.members_count
                        }
                      />
                    </span>
                    <span className="text-sm muted">
                      <FormattedMessage defaultMessage="/ month" />
                    </span>
                  </p>
                </div>
                <p className="mt-0 muted">
                  <FormattedMessage
                    defaultMessage="Currently {membersCount, plural, =1 {# user} other {# users}}"
                    values={{ membersCount: organisation.members_count }}
                  />
                </p>

                <div className="mt-4">
                  <p className="medium mb-1">
                    <FormattedMessage defaultMessage="Payment method" />
                  </p>
                  {addCard ? (
                    <Controller
                      control={control}
                      name="card"
                      defaultValue={false}
                      rules={{
                        required: intl.formatMessage({
                          defaultMessage: 'Please enter your payment details',
                        }),
                      }}
                      render={({ onChange }) => (
                        <div className="mb-4">
                          <CreditCardForm
                            onChange={(event) => {
                              if (event.error) {
                                setError('card', {
                                  message: event.error.message,
                                });
                                onChange(false);
                              } else if (!event.complete) {
                                onChange(false);
                              } else {
                                onChange(true);
                              }
                            }}
                          />
                          {errors.card && (
                            <p className="danger small">
                              {errors.card.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  ) : (
                    <Card className="p-1 pl-3 mb-4 flex items-center">
                      <div>
                        <Icon icon="credit-card" className="mr-2" />
                        <span className="font-mono">
                          {billing.card.brand.toUpperCase()} **** **** ****{' '}
                          {billing.card.last4}
                        </span>
                      </div>
                      <Button
                        iconLeft="edit"
                        minimal
                        className="inline-flex ml-auto"
                        onClick={() => setAddCard(true)}
                      />
                    </Card>
                  )}

                  <Button
                    intent="info"
                    primary
                    className="font-semibold px-6"
                    onClick={handleSubmit(onSubmit)}
                  >
                    <FormattedMessage defaultMessage="Subscribe to this plan" />
                  </Button>
                  <p className="text-xs muted pt-2">
                    <FormattedMessage
                      defaultMessage="By subscribing you agree to our <TermsLink>terms and agreements</TermsLink>"
                      values={{
                        TermsLink: (chunk: string) => (
                          // eslint-disable-next-line react/jsx-no-target-blank
                          <a
                            href={config.termsUrl}
                            target="_blank"
                            className="link text-xs"
                          >
                            {chunk}
                          </a>
                        ),
                      }}
                    />
                  </p>
                </div>
              </div>
            </Card>
            <PoweredByStripe className="mt-4 fill-current text-gray-400" />
          </>
        )}
      </Container>
    </SideSheet>
  );
};

export const UpgradeSideSheet: React.FC<IUpgradeSideSheetProps> = (props) => (
  <StripeWrapper>
    <UpgradeSideSheetWrapped {...props} />
  </StripeWrapper>
);
